<script setup lang="ts">
import { ElNotification } from 'element-plus'
import giftBoxPreview from '@/assets/components/registerComponent/gift_box-preview.webp'
import giftBoxImg from '@/assets/components/registerComponent/gift_box.webp'
import { userDrawer } from '@/components/goat/layout/drawers/User'
import { siteUtils } from '@/compositions/siteUtils'
import {
  confirmPasswordValidator,
  onChangePassword,
  passwordValidator
} from '@/compositions/validators'
import { useGoatStore } from '@/stores/goat'
import { ModeEnum } from '@/types/enums'

const { t } = useI18n()
const auth = useAuthStore()
const goat = useGoatStore()

const { screens } = tools
function logOut(): void {
  userDrawer.value = false
  nextTick(() => {
    auth.logout()
  })
}
const modelCollapse = ref('')
const removingAccountStep2 = ref(false)
const loadingChangePassword = ref(false)
function onSubmitChangePassword(data: FormTypes.SubmitForm): void {
  loadingChangePassword.value = true
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchFormErrors } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()
    ?.post(getGoatApiUrl('logged/change-password'), { ...data.form })
    .then((response) => {
      switch (response?.data?.status ?? '') {
        case 'ok':
          ElNotification({
            title: t('userDrawer.notify.passwordChanged.title'),
            type: 'success',
            duration: 5000,
            offset: 100,
            appendTo: '#change-password-form',
            customClass: 'right-1/2 translate-x-1/2 large absolute'
          })
          data?.formRefer?.resetFields()
          break
        default:
          ElNotification({
            title: t('userDrawer.notify.somethingWentWrong.title'),
            type: 'error',
            duration: 9000,
            offset: 100,
            appendTo: '#change-password-form',
            customClass: 'right-1/2 translate-x-1/2 large absolute'
          })
      }
    })
    .catch(catchFormErrors(data.externalErrors, { appendTo: '#change-password-form' }))
    .then(() => {
      loadingChangePassword.value = false
    })
}
const loadingChangeConsents = ref(false)
const { gaChangeCommunication } = gaUtils
function onSubmitChangeConsents(data: FormTypes.SubmitForm) {
  loadingChangeConsents.value = true
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchFormErrors } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()
    ?.post(getGoatApiUrl('logged/change-communication'), { ...data.form })
    .then((response) => {
      switch (response?.data?.status ?? '') {
        case 'ok':
          gaChangeCommunication({ gs_context: `acceptCommunication=${!!data.form.acceptCommunication}` })
          auth.updateCom(!!data.form.acceptCommunication)
          ElNotification({
            title: t('userDrawer.notify.consentsChanged.title'),
            type: 'success',
            duration: 5000,
            offset: 100,
            appendTo: '#change-consents-form',
            customClass: 'right-1/2 translate-x-1/2 large absolute w-11/12'
          })
          break
        default:
          ElNotification({
            title: t('userDrawer.notify.somethingWentWrong.title'),
            type: 'error',
            duration: 9000,
            offset: 100,
            appendTo: '#change-consents-form',
            customClass: 'right-1/2 translate-x-1/2 large absolute w-11/12'
          })
      }
    })
    .catch(catchFormErrors(data.externalErrors, { appendTo: '#change-consents-form' }))
    .then(() => {
      loadingChangeConsents.value = false
    })
}
function onClosed() {
  modelCollapse.value = ''
  removingAccountStep2.value = false
}
const { gaRemoveAccount } = gaUtils
const loadingRemoveAccount = ref(false)
function removeAccountStep1() {
  removingAccountStep2.value = true
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchError } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()
    ?.post(getGoatApiUrl('logged/remove-account-code'))
    .then((response) => {
      switch (response?.data?.status ?? '') {
        case 'confirm_email_code':
          break
        default:
          removingAccountStep2.value = false
          setTimeout(() => {
            ElNotification({
              title: t('userDrawer.notify.somethingWentWrong.title'),
              type: 'error',
              duration: 9000,
              offset: 100,
              appendTo: '#change-consents-form',
              customClass: 'right-1/2 translate-x-1/2 large absolute'
            })
          }, 500)
      }
    })
    .catch((err) => {
      removingAccountStep2.value = false
      catchError({ appendTo: '#change-consents-form' })(err)
    })
}
function removeAccountStep2(data: FormTypes.SubmitForm) {
  loadingRemoveAccount.value = true
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchError } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()
    ?.post(getGoatApiUrl('logged/remove-account'), { ...data.form })
    .then((response) => {
      switch (response?.data?.status ?? '') {
        case 'ok':
          gaRemoveAccount()
          removingAccountStep2.value = false
          logOut()
          setTimeout(() => {
            siteUtils.updateMode(ModeEnum.register)
            ElNotification({
              title: t('userDrawer.notify.accountRemoved.title'),
              type: 'success',
              duration: 5000,
              offset: 100,
              customClass: 'right-1/2 translate-x-1/2 large absolute',
              appendTo: '#auth-wrapper'
            })
          }, 500)
          break
        case 'token_does_not_match':
          ElNotification({
            title: t('userDrawer.notify.invalidCode.title'),
            type: 'error',
            duration: 9000,
            offset: 100,
            appendTo: '#remove-account-form',
            customClass: 'right-1/2 translate-x-1/2 large absolute'
          })
          break
        default:
          ElNotification({
            title: t('userDrawer.notify.somethingWentWrong.title'),
            type: 'error',
            duration: 9000,
            offset: 100,
            appendTo: '#remove-account-form',
            customClass: 'right-1/2 translate-x-1/2 large absolute'
          })
      }
    })
    .catch(catchError({ appendTo: '#remove-account-form' }))
    .then(() => {
      loadingRemoveAccount.value = false
    })
}
</script>

<template>
  <el-drawer
    v-model="userDrawer"
    title=""
    :size="screens.md.alLeast ? '40%' : '95%'"
    class="user-drawer bg-gs-body-bg"
    @closed="onClosed"
  >
    <template #header>
      <div class="grid grid-cols-[clamp(30px,2.75rem,50px)_1fr] gap-2 items-center max-w-[90%]">
        <el-image
          v-if="goat.getGoatDetails?.avatar"
          class="size-8 big:size-10  rounded-full overflow-hidden"
          :src="`data:image/png;base64,${goat.getGoatDetails.avatar}`"
        >
          <template #error>
            <i-bxs-user-circle
              class="w-full h-full text-main-footer"
            />
          </template>
        </el-image>
        <i-bxs-user-circle
          v-else
          class="size-11 text-main-footer"
        />
        <div class="break-all leading-tight pr-2 text-main-footer">
          {{ auth.getEmail }}
        </div>
      </div>
    </template>
    <template #default>
      <div class="big:pr-8">
        <div class="text-right mb-1">
          <el-button
            link
            class="text-base force-el-plus"
            @click="logOut"
          >
            {{ t('userDrawer.logout') }}<i-iconamoon-enter-bold />
          </el-button>
        </div>
        <el-collapse
          v-model="modelCollapse"
          accordion
          class="bg-gs-body-bg goat-collapse-related text-base"
          @change="removingAccountStep2 = false"
        >
          <!--          <el-collapse-item -->
          <!--            title="Profile Details" -->
          <!--            name="details" -->
          <!--          > -->
          <!--            <div class="mb-2"> -->
          <!--              <div class="text-xs"> -->
          <!--                Email -->
          <!--              </div> -->
          <!--              <div class="text-base"> -->
          <!--                {{ auth.getEmail }} -->
          <!--              </div> -->
          <!--            </div> -->
          <!--          </el-collapse-item> -->
          <el-collapse-item
            :title="t('userDrawer.accountSettings')"
            name="consents"
          >
            <transition
              name="el-fade-in"
              mode="out-in"
            >
              <template v-if="modelCollapse === 'consents'">
                <div
                  v-if="removingAccountStep2"
                  class="form-wrapper"
                >
                  <goat-form-component
                    id="remove-account-form"
                    hide-required-asterisk
                    @submit="removeAccountStep2"
                  >
                    <template #default="{ form, submit }">
                      <goat-form-fields-group>
                        <div class="mb-6 px-2">
                          {{ t('userDrawer.deletionDescription') }}
                        </div>
                      </goat-form-fields-group>
                      <goat-form-fields-group class="mb-5 max-w-80">
                        <goat-form-field-text-trim
                          api-field-name="token"
                          :placeholder="t('userDrawer.confirmationCodePlaceholder')"
                          :label="!!form?.token?.length ? t('userDrawer.confirmationCodePlaceholder') : false"
                          class="mt-1 big:mt-1.5 text-lg"
                          size="large"
                          required
                          maxlength="6"
                          show-word-limit
                          :rules="[{ type: 'string', trigger: 'change', len: 6, message: t('form.validation.sixCharsRequired') }]"
                          @keyup.enter.stop.prevent="submit"
                        />
                      </goat-form-fields-group>
                      <goat-form-fields-group
                        justify="start"
                        class="max-w-80"
                      >
                        <el-button
                          type="info"
                          class="text-white rounded uppercase w-[46%]"
                          @click="removingAccountStep2 = false"
                        >
                          {{ t('common.cancel') }}
                        </el-button>
                        <el-button
                          type="primary"
                          class="text-white rounded uppercase w-[46%]"
                          :loading="loadingRemoveAccount"
                          @click="submit()"
                        >
                          {{ t('common.confirm') }}
                        </el-button>
                      </goat-form-fields-group>
                    </template>
                  </goat-form-component>
                </div>
                <goat-form-component
                  v-else
                  id="change-consents-form"
                  hide-required-asterisk
                  class="max-w-80 relative"
                  @submit="onSubmitChangeConsents"
                >
                  <template #default="{ form, submit }">
                    <goat-form-field-check-box
                      api-field-name="acceptTerms"
                      :label="false"
                      disabled
                      class="text-base font-light ml-0.5 goat-large pt-1"
                      :init-value="true"
                    >
                      <template #label>
                        {{ t('acceptTermsCheckBoxes.iAccept') }} <a
                          href="https://gamesture.com/terms-of-service"
                          class="inline underline underline-offset-4 decoration-1 hover:decoration-2 whitespace-nowrap"
                          rel="nofollow"
                          target="_blank"
                        >
                          {{ t('acceptTermsCheckBoxes.termsLink') }}
                        </a>,
                        <a
                          href="https://gamesture.com/privacy-policy"
                          class="inline underline underline-offset-4 decoration-1 hover:decoration-2 whitespace-nowrap"
                          rel="nofollow"
                          target="_blank"
                        >
                          {{ t('acceptTermsCheckBoxes.privacyLink') }}
                        </a>
                      </template>
                    </goat-form-field-check-box>
                    <goat-form-field-check-box
                      api-field-name="acceptCommunication"
                      :label="false"
                      :init-value="auth.getCom"
                      class="text-base font-light ml-0.5 goat-large"
                    >
                      <template #label>
                        <div class="grid grid-cols-[clamp(40px,26%,120px)_1fr] gap-2">
                          <div class="col-span-2 font-semibold">
                            {{ t('acceptTermsCheckBoxes.communication.title') }}
                          </div>
                          <div class="w-full">
                            <el-image
                              :src="giftBoxImg"
                              lazy
                              alt="benefit icon"
                              class="width-full"
                              :class="{ grayscale: !form.acceptCommunication }"
                            >
                              <template #placeholder>
                                <img
                                  :src="giftBoxPreview"
                                  alt="benefit icon"
                                  class="w-full"
                                >
                              </template>
                            </el-image>
                          </div>
                          <div class="">
                            <div class="max-w-60 big:leading-[1.3em]">
                              {{ t('acceptTermsCheckBoxes.communication.description') }}
                            </div>
                          </div>
                        </div>
                      </template>
                    </goat-form-field-check-box>
                    <goat-form-fields-group
                      justify="space-between"
                    >
                      <el-popconfirm
                        :title="t('userDrawer.removeConfirm')"
                        icon-color="#3ba6bc"
                        cancel-button-type="info"
                        @confirm="removeAccountStep1"
                      >
                        <template #reference>
                          <el-button
                            type="info"
                            class="text-white uppercase rounded-md flex-1"
                          >
                            {{ t('userDrawer.removeAccount') }}
                          </el-button>
                        </template>
                      </el-popconfirm>
                      <el-popconfirm
                        v-if="!form.acceptCommunication"
                        :title="t('userDrawer.withdrawConsent')"
                        placement="top"
                        width="250px"
                        :offset="-5"
                        icon-color="#3ba6bc"
                        :cancel-button-text="t('userDrawer.withdrawConsentCancel')"
                        :confirm-button-text="t('userDrawer.withdrawConsentConfirm')"
                        confirm-button-type="primary"
                        cancel-button-type="info"
                        @confirm="submit()"
                        @cancel="form.acceptCommunication = true"
                      >
                        <template #reference>
                          <el-button
                            type="primary"
                            class="text-white rounded-md uppercase flex-1"
                            :loading="loadingChangeConsents"
                          >
                            {{ t('userDrawer.changeBtn') }}
                          </el-button>
                        </template>
                      </el-popconfirm>
                      <el-button
                        v-else
                        type="primary"
                        class="text-white rounded-md uppercase flex-1"
                        :loading="loadingChangeConsents"
                        @click="submit()"
                      >
                        {{ t('userDrawer.changeBtn') }}
                      </el-button>
                    </goat-form-fields-group>
                  </template>
                </goat-form-component>
              </template>
            </transition>
          </el-collapse-item>
          <el-collapse-item
            v-if="!!goat.getGoatDetails?.password_set"
            :title="t('userDrawer.changePasswordTitle')"
            name="passwords"
          >
            <goat-form-component
              v-if="modelCollapse === 'passwords'"
              id="change-password-form"
              hide-required-asterisk
              class="max-w-80 relative"
              @submit="onSubmitChangePassword"
            >
              <template #default="{ submit, form, formRefer }">
                <goat-form-field-text-trim
                  api-field-name="NewPassword"
                  :label="t('userDrawer.newPasswordLabel')"
                  type="password"
                  show-password
                  :rules="[passwordValidator()]"
                  @input="onChangePassword(form, formRefer, 'confirm_password')"
                  @keyup.enter.stop.prevent="submit"
                />
                <goat-form-field-text-trim
                  api-field-name="confirm_password"
                  :label="t('userDrawer.confirmPasswordLabel')"
                  type="password"
                  show-password
                  :rules="[confirmPasswordValidator(form, 'NewPassword')]"
                  @keyup.enter.stop.prevent="submit"
                />
                <goat-form-fields-group
                  justify="space-between"
                >
                  <el-button
                    type="info"
                    class="text-white rounded-md uppercase w-[46%]"
                    @click="formRefer?.resetFields()"
                  >
                    {{ t('userDrawer.clearBtn') }}
                  </el-button>
                  <el-button
                    type="primary"
                    class="text-white rounded-md uppercase w-[46%]"
                    :loading="loadingChangePassword"
                    @click="submit()"
                  >
                    {{ t('userDrawer.changeBtn') }}
                  </el-button>
                </goat-form-fields-group>
              </template>
            </goat-form-component>
          </el-collapse-item>
        </el-collapse>
      </div>
    </template>
  </el-drawer>
</template>

<style>
@layer components {
  .user-drawer {
      .el-drawer__header {
          padding-top: 1rem;
          margin-bottom: 0.3rem;
          background: inherit;
      }

      .el-drawer__body {
        padding-top: 0;
        background: inherit;
      }

    .el-collapse {
      --el-collapse-header-bg-color : #f7f7f7;
      --el-collapse-content-bg-color: #f7f7f7;
    }

    .form-wrapper {
      --el-component-size-large: 44px;
      --el-border-radius-base: 6px;

      .el-form-item {
        position: relative;

        .el-form-item__label {
          padding-left: 5px;
          position: absolute;
          top: -15px;
          margin: 0;
          font-size: 0.6em;
        }
      }
    }
  }
}
</style>
